// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authGet, logout as apiLogout } from '../services/authApi';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);
    const isLoggedIn = sessionStorage.getItem('isLoggedIn');

    if (!isLoggedIn) {
      setIsLoading(false);
      return;
    }

    try {
      // This endpoint should return user data if the session is valid
      const response = await authGet('/api/auth/session');
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        setIsAdmin(userData.role === 'admin');
        sessionStorage.setItem('isLoggedIn', 'true');
      } else {
        // If the session is not valid, ensure client-side state is cleared.
        // We DO NOT call apiLogout() here. A failed session check (e.g. from a network error)
        // should not destroy a potentially valid server session.
        setUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
        sessionStorage.removeItem('isLoggedIn');
      }
    } catch (error) {
      logger.error('Authentication check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      sessionStorage.removeItem('isLoggedIn');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = (userData) => {
    // This function is called after a successful login API call.
    // It sets the user state directly from the login response.
    setUser(userData);
    setIsAuthenticated(true);
    setIsAdmin(userData.role === 'admin');
    sessionStorage.setItem('isLoggedIn', 'true');
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    sessionStorage.removeItem('isLoggedIn');
    window.location.href = '/login'; // Redirect after state is cleared
  };

  // I've added checkAuthStatus here as a recommended practice.
  const value = { user, isAuthenticated, isAdmin, isLoading, login, logout, checkAuthStatus };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};